import { FixLogProps, LogFetchParam } from '@@types/Api/Log';
import Channel from '@@types/Channel';
import { CHANNEL_ID, LOG_ID, REACTION_ID } from '@@types/common';
import Log, { AllLog, ForwardDummyLog, ForwardLog, ReactionsLog, ReplyDummyLog, TextDummyLog } from '@@types/Log';
import LogsResponse from '@@types/Log/Response';
import LogFile from '@@types/LogFile';

import { LOG_TYPE, LogFetchDirection, SERVER_ERROR, UNREAD_LOG_ID } from '~/constants';
import i18n from '~/i18n';
import GA from '~/services/GA/GA';
import MainEditorActions from '~/stores/actions/MainEditorActions';
import ChannelLogsStore from '~/stores/ChannelLogsStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelsStore } from '~/stores/ChannelsStore';
import DetailUiState, { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { replyLogStore } from '~/stores/ReplyLogStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import {
  canCreateThreadLog,
  createDummyLog,
  createForwardDummyLog,
  createReplyDummyLog,
  createStickerDummyLog,
  createStickerReplyDummyLog,
  getForwardedLog,
  isForwardLog,
} from '~/utils/channelLogsUtil';
import domUtil from '~/utils/domUtil';
import { makeDownloadUrl, validateFileForbidden } from '~/utils/fileLogUtil';
import { isNonEmpty, isNonNil, rename, wrapLoading } from '~/utils/fnUtil/fnUtil';
import { mEither, runFutureFork } from '~/utils/monadUtil';
import Future, { FutureInstance } from 'fluture';
import { isEmpty, throttle } from 'lodash';
import { action, runInAction } from 'mobx';
import {
  assoc,
  concat,
  cond,
  converge,
  equals,
  head,
  last,
  map,
  mergeLeft,
  objOf,
  pick,
  pipe,
  pluck,
  propSatisfies,
  values,
  when,
} from 'ramda';
import curry from 'ramda/es/curry';
import mergeRight from 'ramda/es/mergeRight';
import tap from 'ramda/es/tap';
import { Either, Maybe } from 'ramda-fantasy';

import Actions from '../Actions';

const getFetchNextLogsParams = pipe(pick(['channelId', 'seq']), assoc('direction', LogFetchDirection.Next));

const getFetchPrevLogsParams = pipe(pick(['channelId', 'seq']), assoc('direction', LogFetchDirection.Prev));

const getFetchUnreadLogsParams: (c: Channel) => any = pipe(
  pick(['channelId', 'readSeq']),
  rename({ readSeq: 'seq' }),
  assoc('direction', LogFetchDirection.Both)
);

class LogActions {
  public replyLog = curry((targetLog: AllLog, dummyLog: ReplyDummyLog & FixLogProps) => {
    api.log.replyLog(targetLog, dummyLog);
  });

  private prevScrollTop = 0;
  private channelLogsStore: ChannelLogsStore;
  private mainEditorActions: MainEditorActions;
  private detailUiState: DetailUiState;
  private context: 'main' | 'thread';
  private sendLog = curry((channelId: string, dummyLog: TextDummyLog) => {
    this.actions.proxyActions.sendLog(assoc('channelId', channelId, dummyLog));
  });

  private setScrollTargetLog = domUtil.singletonRequestAnimationFrame((log) => {
    this.detailUiState.setScrollTargetLog(log);
  });

  constructor(
    private actions: Actions,
    {
      channelLogsStore,
      mainEditorActions,
      detailUiState,
      context,
    }: {
      channelLogsStore: ChannelLogsStore;
      mainEditorActions: MainEditorActions;
      detailUiState: DetailUiState;
      context: 'main' | 'thread';
    }
  ) {
    this.channelLogsStore = channelLogsStore;
    this.mainEditorActions = mainEditorActions;
    this.detailUiState = detailUiState;
    this.context = context;
  }

  public createAndSendLog = (channelId: string, text: string): void => {
    const { seq } = channelManager.getById(channelId);
    const { id: senderId } = userStore;
    pipe<string, any, any, any, any, any>(
      this.validateLogText,
      map(createDummyLog({ channelId, senderId, seq })), // 더미 로그 생성
      map(tap(this.detailUiState.setScrollTargetLog)), // 스크롤 타겟 지정
      map(tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.appendDummyLog))), // 더미로그 추가
      mEither(this.alertError, this.sendLog(channelId))
    )(text);
  };

  public sendSticker = (channelId: string, stickerObj) => {
    const replyLog = replyLogStore.getByChannelId(channelId);
    const forwardLog = forwardLogStore.getByChannelId(channelId);
    GA.writeSendSticker({ action: String(stickerObj.stickerPackId), label: String(stickerObj.stickerId) });
    if (isNonNil(replyLog)) {
      this.createAndReplySticker(channelId, replyLog, stickerObj);
    } else if (isNonNil(forwardLog)) {
      this.sendForwardLog(channelId, forwardLog).then(() => this.createAndSendSticker(channelId, stickerObj));
    } else {
      this.createAndSendSticker(channelId, stickerObj);
    }
  };

  public removeForwardLog = (channelId: string) => {
    forwardLogStore.removeForwardLog(channelId);
    this.mainEditorActions.focus();
  };

  public sendForwardLog = curry((channelId: string, log: Log) => {
    const { seq } = channelManager.getById(channelId);
    const channelTitle = this.actions.channelActions.getTitle({ channelId: log.channelId });

    return pipe<any, any, any, any, any>(
      createForwardDummyLog({ channelId, seq, senderId: userStore.id }, channelTitle),
      tap(this.detailUiState.setScrollTargetLog),
      // channel이 먼저 변경된 경우 append하지 않음
      tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.appendDummyLog)),
      (dummyLog: ForwardDummyLog) => {
        const channelTitle = this.actions.channelActions.getTitle({ channelId: log.channelId });
        forwardLogStore.removeForwardLog(channelId);
        return api.log.forwardLog(channelId, channelTitle, assoc('token', dummyLog.token, log)).catch((error) => {
          this.channelLogsStore.removeLog(dummyLog.id);
          return Promise.reject(error);
        });
      }
    )(log);
  });

  public createAndReplyLog = (channelId, targetLog: AllLog, text: string) => {
    const { seq } = channelManager.getById(channelId);
    const { id: senderId } = userStore;
    pipe<any, any, any, any, any, any, any>(
      this.validateLogText,
      map(createReplyDummyLog({ channelId, senderId, seq }, targetLog.id)), // 더미 로그 생성
      map(tap(this.detailUiState.setScrollTargetLog)), // 스크롤 타겟 지정
      map(tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.appendDummyLog))), // 더미로그 추가
      map(mergeLeft({ type: LOG_TYPE.TEXT, text })),
      mEither(this.alertError, this.replyLog(targetLog))
    )(text);
  };

  public createAndReplySticker = (channelId: string, targetLog: AllLog, stickerObj: any) => {
    const { seq } = channelManager.getById(channelId);
    const { id: senderId } = userStore;
    pipe<any, any, any, any, any, any>(
      createStickerReplyDummyLog({ channelId, senderId, seq }, targetLog.id),
      tap(this.detailUiState.setScrollTargetLog),
      tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.appendDummyLog)),
      mergeLeft({ type: LOG_TYPE.STICKER, text: JSON.stringify(stickerObj) }),
      this.replyLog(targetLog)
    )(stickerObj);
    replyLogStore.removeReplyLog(channelId);
  };

  public alertError = (error) => {
    pipe<string, string, any, any>(
      cond<any, any>([
        [
          equals('max length'),
          () =>
            i18n.t('sendMessage.overMaxLength', {
              limit: globalSettingStore.logTextMaxLength,
            }),
        ],
        [equals('forbidden file'), () => i18n.t('SERVER_FILE_DOWNLOAD_FORBIDDEN_EXTENSIONS_ERROR')],
      ]),
      objOf('text'),
      when(propSatisfies(isNonEmpty, 'text'), this.actions.notificationActions.showError)
    )(error);
  };

  public fetchLatestLogsIfNeeded = (channelId: string) => {
    const logs = this.channelLogsStore.logList as Log[];
    return this.channelLogsStore.isLastLogLoaded
      ? Promise.resolve({
          channelId,
          logs,
        })
      : this.fetchLatestLogs(channelId);
  };

  public fetchLatestLogsOrScrollBottom = (channelId: string) => {
    if (this.channelLogsStore.isLastLogLoaded) {
      this.detailUiState.scrollToBottom();
    } else {
      this.fetchLatestLogs(channelId);
    }
  };

  public fetchLatestLogs = (channelId: string) => {
    return this.fetchAndSet({ channelId }, ({ result: { channelId, logs } }: LogsResponse) => {
      this.detailUiState.scrollToBottom();
      this.actions.memberActions.fetchLeaveMembersByLogs(logs);
      this.updateReadSeq(channelId);
    });
  };

  download = (file: { id: string; channelId: CHANNEL_ID; forbiddenExtensionFlag?: boolean }, e: React.MouseEvent) => {
    const isSharedChannel = channelsStore.isSharedChannelId(file.channelId);
    e.persist();
    e.stopPropagation();
    e.preventDefault();
    pipe<any, any, any, any, any>(
      validateFileForbidden,
      mEither(Future.reject, this.checkFileAcl),
      map((file: { id: string; channelId: CHANNEL_ID }) => makeDownloadUrl(file, isSharedChannel)),
      runFutureFork(this.alertError, this.setDownloadIframe)
    )(file);
  };

  public fetchHighlightLogs = (params: { logId: string; channelId: string }) => {
    const logId = params.logId;
    this.detailUiState.setHighlightTarget(logId);
    if (this.channelLogsStore.getMainLogById(logId)) {
      this.detailUiState.setScrollTarget(logId);
    } else {
      this.fetchAndSet(mergeRight({ direction: LogFetchDirection.Both }, params), () => {
        this.detailUiState.setScrollTarget(logId);
      });
    }
  };

  public handleClickReplyLog = (originalLog: AllLog, replyLog: AllLog, isThread: boolean) => {
    if (
      originalLog.channelId !== (isThread ? threadDetailUiState.contentsChannelId : mainDetailUiState.contentsChannelId)
    ) {
      channelsStore.setPreventFetchLogs(true);
      this.actions.channelActions.selectChannel(originalLog.channelId);
    }
    this.fetchHighlightLogs({ logId: originalLog.id, channelId: originalLog.channelId });
    if (!this.channelLogsStore.newLogId) {
      this.detailUiState.setReplyReturnLog(replyLog);
    }
  };

  public handleScrollArea = (node: HTMLDivElement) => {
    this.fetchLogsByScroll(node.scrollTop);
    if (this.detailUiState.scrollAreaInfo.isScrollBottom) {
      this.setNewLog(null);
    }
  };

  public getReplyOriginLog = (log: Log): Maybe<Log> => {
    return this.channelLogsStore.safeGetOriginalLogById('originalLogId' in log && log.originalLogId);
  };

  @action
  public setNewLog = (log: Log | null) => {
    this.channelLogsStore.setNewLog(log);
    if (log && this.detailUiState.replyReturnLog) {
      this.detailUiState.setReplyReturnLog(null);
    }
  };

  @action
  private setLastReadLogSeq = (channelId: CHANNEL_ID) => {
    this.channelLogsStore.clearLastReadLogSeq(channelId);
    const readSeq = channelManager.getChannelReadSeq(channelId);
    if (readSeq !== null) {
      this.channelLogsStore.setLastReadLogSeq(channelId, readSeq);
    }
  };

  public setForwardLogById = async (channelId: string, forwardLogChannelId: string, forwardLogId: string) => {
    const { result } = await this.actions.proxyActions.fetchLogs({
      channelId: forwardLogChannelId,
      limit: 1,
      setLastChannel: false,
      logId: forwardLogId,
      direction: LogFetchDirection.Both,
    });
    forwardLogStore.setForwardLog(channelId, result?.logs[0] as ForwardLog);
  };

  public fetchUnreadLogs = (channel: Channel) => {
    return this.fetchAndSet(getFetchUnreadLogsParams(channel), ({ result: { channelId, logs } }: LogsResponse) => {
      const newLog = logs.find(propSatisfies(equals(channel.seq), 'seq'));
      if (newLog) {
        this.setNewLog(newLog);
      } else {
        this.fetchNewLog(channel.channelId);
      }
      this.setLastReadLogSeq(channelId);
      this.updateReadSeq(channelId);
      this.detailUiState.setScrollTarget(`${channel.channelId}-${UNREAD_LOG_ID}`);
    });
  };

  private fetchNewLog = (channelId: CHANNEL_ID) => {
    this.actions.proxyActions.fetchLogs({ channelId, limit: 1 }).then((response) => {
      const newLog = response?.result?.logs?.[0];
      this.setNewLog(newLog);
    });
  };

  public fetchNextLogs = () => {
    return wrapLoading(
      async () => {
        const lastLog = last(this.channelLogsStore.logList);
        if (!lastLog) {
          return;
        }
        const res = await this.actions.proxyActions.fetchLogs(getFetchNextLogsParams(lastLog));
        runInAction(() => {
          this.handleFetchLogs(res);
        });
      },
      (loading) => {
        this.channelLogsStore.setIsFetchingLogs(loading);
        this.channelLogsStore.setIsFetchingEdgeLogs({ next: loading });
      }
    );
  };

  public fetchPrevLogs = () => {
    return wrapLoading(
      async () => {
        const firstLog = head(this.channelLogsStore.logList);
        if (!firstLog) {
          return;
        }
        const res = await this.actions.proxyActions.fetchLogs(getFetchPrevLogsParams(firstLog));
        runInAction(() => {
          this.handleFetchLogs(res);
          // 추가된 이후 현재 보던 위치로 이동
          this.setScrollTargetLog(firstLog);
        });
      },
      (loading) => {
        this.channelLogsStore.setIsFetchingLogs(loading);
        this.channelLogsStore.setIsFetchingEdgeLogs({ prev: loading });
      }
    );
  };

  private fetchLogsByScroll = throttle((scrollTop) => {
    if (scrollTop > this.prevScrollTop) {
      if (
        this.detailUiState.scrollAreaInfo.isScrollBottom &&
        !this.channelLogsStore.isLastLogLoaded &&
        !this.channelLogsStore.isFetchingEdgeLogs.next
      ) {
        this.fetchNextLogs();
      }
    } else if (scrollTop < this.prevScrollTop) {
      if (
        scrollTop === 0 &&
        !this.channelLogsStore.isFirstLogLoaded &&
        !this.channelLogsStore.isFetchingEdgeLogs.prev
      ) {
        this.fetchPrevLogs();
      }
    }
    this.prevScrollTop = scrollTop;
  }, 50);

  private createAndSendSticker = (channelId: string, stickerObj) => {
    const { seq } = channelManager.getById(channelId);
    const { id: senderId } = userStore;
    this.mainEditorActions.focus();
    pipe<string, any, any, any, any, any>(
      createStickerDummyLog({ channelId, senderId, seq }),
      tap(this.detailUiState.setScrollTargetLog),
      tap(this.detailUiState.whenCurrentChannel(channelId, this.channelLogsStore.appendDummyLog)),
      assoc('channelId', channelId),
      api.sticker.sendSticker
    )(stickerObj);
  };

  private validateLogText = (text: string) => {
    if (isEmpty(text)) {
      return Either.Left('empty');
    } else if (text.length > globalSettingStore.logTextMaxLength) {
      return Either.Left('max length');
    } else {
      return Either.Right(text);
    }
  };

  private checkFileAcl = (file: LogFile): FutureInstance<string, LogFile> => {
    return Future((reject, resolve) => {
      this.actions.proxyActions
        .checkFileAcl(file.channelId, file.id)
        .then(() => resolve(file))
        .catch((error) => {
          if (
            [
              SERVER_ERROR.AUTH_FORBIDDEN_FILE_ACL_IP_ERROR,
              SERVER_ERROR.AUTH_FORBIDDEN_FILE_ACL_SERVICE_ERROR,
            ].includes(error.data.header.resultCode)
          ) {
            return reject('');
          }

          return reject('forbidden file');
        });
    });
  };

  private setDownloadIframe = (url) => {
    const iframe = document.getElementById('js-download-iframe') as HTMLIFrameElement;
    iframe.src = url;
  };

  private getOriginalLogs = (response: LogsResponse): Log[] => {
    return values(response.references?.originalLogMap ?? {});
  };

  private getForwardedLogs = (response: LogsResponse): Log[] => {
    return response.result.logs
      .map((log) => {
        return isForwardLog(log) ? getForwardedLog(log).getOrElse(null) : null;
      })
      .filter(isNonNil);
  };

  private fetchMetaLogsSenders = (response: LogsResponse) => {
    pipe<any, any, any, any>(
      converge<any, any, any, any>(concat, [this.getOriginalLogs, this.getForwardedLogs]),
      pluck('senderId'),
      this.actions.memberActions.fetchBulkMembers
    )(response);
  };

  private handleFetchLogs = (res: LogsResponse) => {
    this.fetchMetaLogsSenders(res);
    this.channelLogsStore.setPrevLoaded(res.result.hasPrev ? null : res.result.channelId);
    this.channelLogsStore.mergeLogs(res?.result?.logs);
    this.channelLogsStore.mergeReferenceLogMap(res?.references?.originalLogMap ?? {});
    this.channelLogsStore.mergeReactionMap(res?.references?.reactionMap ?? {});
  };

  private fetchAndSet = (params: LogFetchParam, handleRes: (res: LogsResponse) => void) => {
    return wrapLoading(
      async () => {
        const res = await this.actions.proxyActions.fetchLogs(params);
        runInAction(() => {
          this.channelLogsStore.setPrevLoaded(res.result.hasPrev ? null : params.channelId);
          this.channelLogsStore.setIsFirstLoaded(res.result.isFirst);
          this.detailUiState.setFetchedContentsChannelId(params.channelId);
          this.setNewLog(null);
          this.fetchMetaLogsSenders(res);
          const logs = res?.result?.logs ?? [];
          this.channelLogsStore.setLogs(logs);
          this.channelLogsStore.setReferenceLogMap(res?.references?.originalLogMap ?? {});
          this.channelLogsStore.mergeReactionMap(res?.references?.reactionMap ?? {});
          if (!globalSettingStore.isMakeThreadComplete) {
            const myId = userStore.id;
            const lastLogId = last(logs.filter((log) => canCreateThreadLog(log) && log.senderId !== myId))?.id;
            if (lastLogId) {
              this.actions.coachMarkActions.startMakeThreadCoachMark();
              uiState.setHoverTriggerLog(lastLogId);
            }
          }
          // action 1개로 묶기 위해 수정
          handleRes(res);
        });
        return res;
      },
      (loading) => {
        this.channelLogsStore.setIsFetchingLogs(loading);
        this.channelLogsStore.setIsLoadingLogs(loading);
      }
    );
  };

  private updateReadSeq = (channelId) => {
    const channel = channelManager.getById(channelId);
    this.actions.channelMembersActions.updateChannelReadSeq(channel);
  };

  public toggleEmoticon = ({
    logId,
    channelId,
    emoticon,
    reactionId,
  }: {
    logId: LOG_ID;
    channelId: CHANNEL_ID;
    emoticon?: string;
    reactionId?: REACTION_ID;
  }) => {
    if (!reactionId) {
      const log: ReactionsLog = this.channelLogsStore.getById(logId) as ReactionsLog;
      reactionId = log?.reactions?.find(
        (reactionId) => this.channelLogsStore.getReaction(reactionId)?.value === emoticon
      );
    }
    const reaction = this.channelLogsStore.getReaction(reactionId);
    if (!reaction) {
      return this.attachReaction({ logId, channelId, reactionId, emoticon });
    }
    const index = reaction.members.findIndex(({ memberId }) => memberId === userStore.id);
    if (index > -1) {
      return this.detachReaction(reactionId, { logId, channelId });
    }
    return this.attachReaction({ logId, channelId, reactionId, emoticon });
  };

  public attachReaction = ({
    logId,
    channelId,
    reactionId,
    emoticon,
  }: {
    logId: LOG_ID;
    channelId: CHANNEL_ID;
    reactionId?: REACTION_ID;
    emoticon?: string;
  }) => {
    if (!emoticon) {
      emoticon = this.channelLogsStore.getReaction(reactionId)?.value;
    }
    if (!emoticon) {
      return;
    }
    const log = this.channelLogsStore.getById(logId);
    if ('reactions' in log) {
      if (
        log.reactions.length === 10 &&
        log.reactions.findIndex((reactionId) => this.channelLogsStore.getReaction(reactionId)?.value === emoticon) ===
          -1
      ) {
        this.actions.modalActions.openAlert({
          description: i18n.t('reaction.max-alert', '리액션은 최대 10개 까지 등록 가능합니다.'),
        });
        return;
      }
    }
    if (reactionId) {
      const reaction = this.channelLogsStore.getReaction(reactionId);
      this.channelLogsStore.setReaction(reactionId, {
        ...reaction,
        members: [...reaction.members, { id: reactionId, memberId: userStore.id }],
      });
    }
    api.log.attachReaction({ channelId, logId, emoticon });
  };

  public detachReaction = (reactionId: REACTION_ID, { logId, channelId }: { logId: LOG_ID; channelId: CHANNEL_ID }) => {
    const reaction = this.channelLogsStore.getReaction(reactionId);
    if (!reaction) {
      return;
    }
    const index = reaction.members.findIndex(({ memberId }) => memberId === userStore.id);
    if (index > -1) {
      this.channelLogsStore.setReaction(reactionId, {
        ...reaction,
        members: [...reaction.members.slice(0, index), ...reaction.members.slice(index + 1)],
      });
      api.log.detachReaction({ channelId, logId, reactionId });
    }
  };
}

export default LogActions;
